/**
 * Pure helpers for the Finance charges page (tracker item 28). Charges come
 * from the server as decimal strings; the only client arithmetic is the total
 * of the selected rows, done in integer cents.
 */

import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { formatRatePercent } from '../utils/taxRate';
import { invoiceMoney } from './invoiceUi';
import { localDateOnly } from './paymentUi';

export const FINANCE_CHARGES_PATH = '/invoices/finance-charges';
export const COMPANY_SETTINGS_PATH = '/settings/company';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The latest as-of date the server accepts. The backend compares against
 * today in UTC, so a user ahead of UTC would get a 400 for their local
 * "today"; the earlier of the two is always accepted.
 */
export function maxFinanceChargeDate(now: Date = new Date()): string {
  const local = localDateOnly(now);
  const utc = now.toISOString().slice(0, 10);
  return local < utc ? local : utc;
}

/** Validation message for the as-of date, or null when it can be previewed. */
export function asOfDateError(value: string, maxDate: string): string | null {
  if (!value) return 'Choose an as-of date.';
  if (!DATE_ONLY.test(value) || Number.isNaN(Date.parse(`${value}T00:00:00Z`))) return 'Enter a valid date.';
  if (value > maxDate) return 'The as-of date cannot be in the future.';
  return null;
}

/** Ids of the rows assess would charge (the only rows that can be selected). */
export function chargeableCustomerIds(rows: types.FinanceChargePreviewRow[]): string[] {
  return rows.filter((row) => row.chargeable).map((row) => row.customerId);
}

/** Toggle one id in a selection, keeping the order stable. */
export function toggleSelection(selected: string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((entry) => entry !== id) : [...selected, id];
}

/** Select-all checkbox: everything chargeable, or nothing when all are selected. */
export function toggleAll(rows: types.FinanceChargePreviewRow[], selected: string[]): string[] {
  const ids = chargeableCustomerIds(rows);
  return ids.length > 0 && ids.every((id) => selected.includes(id)) ? [] : ids;
}

/** Decimal string -> integer cents without float drift ("12.345" is not expected; extra places are truncated). */
export function toCents(value: string): number {
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(value.trim());
  if (!match) return 0;
  const [, sign, whole, fraction = ''] = match;
  const cents = Number(whole || '0') * 100 + Number((fraction + '00').slice(0, 2));
  return sign ? -cents : cents;
}

export function centsToDecimal(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Total charge of the selected chargeable rows, as a 2dp decimal string. */
export function selectedChargeTotal(rows: types.FinanceChargePreviewRow[], selected: string[]): string {
  const cents = rows
    .filter((row) => row.chargeable && selected.includes(row.customerId))
    .reduce((sum, row) => sum + toCents(row.charge), 0);
  return centsToDecimal(cents);
}

/** The text of the confirm dialog before assessing. */
export function assessConfirmMessage(count: number, total: string, preview: Pick<types.FinanceChargePreview, 'asOfDate' | 'periodKey'>): string {
  const noun = count === 1 ? 'customer' : 'customers';
  return `Assess finance charges for ${count} ${noun}, totaling ${invoiceMoney(total)}, as of ${preview.asOfDate}?\n\n` +
    `This creates and posts one finance-charge invoice per customer for ${preview.periodKey}. ` +
    'Each customer can be charged once per month; to waive a charge, cancel its invoice.';
}

/** "1.5% per month, 10 grace days, $5.00 minimum" */
export function financeChargeSettingsSummary(settings: types.FinanceChargeSettingsSnapshot): string {
  const grace = settings.graceDays === 1 ? '1 grace day' : `${settings.graceDays} grace days`;
  const min = toCents(settings.minAmount) > 0 ? `${invoiceMoney(settings.minAmount)} minimum` : 'no minimum';
  return `${formatRatePercent(settings.rate)} per month, ${grace}, ${min}`;
}

const SKIP_REASON_LABELS: Record<types.FinanceChargeSkipReason, string> = {
  ALREADY_ASSESSED: 'Already charged this month',
  CREDIT_COVERS_BALANCE: 'Credit covers balance',
  BELOW_ONE_CENT: 'Charge under one cent',
  NOTHING_OVERDUE: 'Nothing overdue',
  ERROR: 'Error',
};

export function skipReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '';
  return SKIP_REASON_LABELS[reason as types.FinanceChargeSkipReason] ?? reason;
}

export interface FinanceChargeErrorView {
  message: string;
  /** Show a link to Settings > Company (the rate lives there) */
  settingsLink: boolean;
}

/**
 * A user-facing view of a preview / assess failure. Server messages are
 * already readable; the known codes get a message that says what to do next.
 */
export function financeChargeError(err: unknown, fallback: string): FinanceChargeErrorView {
  switch (getErrorCode(err)) {
    case 'FINANCE_CHARGES_DISABLED':
      return {
        message: 'Finance charges are turned off: the finance charge rate in company settings is 0. An administrator can set a rate under Settings > Company.',
        settingsLink: true,
      };
    case 'CUSTOMER_NOT_FOUND':
      return {
        message: 'One of the selected customers no longer exists. Nothing was charged. Refresh the preview and try again.',
        settingsLink: false,
      };
    case 'FINANCE_CHARGE_ACCOUNT_MISSING':
      return {
        message: 'The 4300 Finance Charge Income account is missing from the chart of accounts. Nothing was charged. Ask an administrator to add it.',
        settingsLink: false,
      };
    default:
      return { message: getErrorMessage(err, fallback), settingsLink: false };
  }
}
